import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatPhone, toCents, toE164, type Locale } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { Pager } from '../components/Pager.js';
import { ListToolbar, type FilterChip } from '../components/ListToolbar.js';
import { SortHeader } from '../components/SortHeader.js';
import { downloadExport, useListView } from '../lib/listView.js';

/**
 * The customer list — 7540 records, scanned by a coordinator who is usually on the phone
 * while doing it.
 *
 * Three decisions drive everything on this screen:
 *
 *   - **The phone number is the identifier.** 97% of these customers have one and only 71%
 *     have an email, so the phone gets its own column, in mono, at full prominence — not a
 *     line of grey metadata under the name. Someone reads a number off a caller display and
 *     matches it here, digit by digit.
 *   - **Every filter is a search parameter.** Nothing on this screen is `useState`: the
 *     query text, the city, the tag, the sort and the page cursor all live in the URL, so F5
 *     returns the same list and a filtered view can be pasted into a message to a colleague.
 *   - **Density over comfort.** Five columns, one line of secondary text where it earns its
 *     place (the organisation under the name), and no row taller than it needs to be. A
 *     coordinator should see thirty customers without scrolling.
 */

// --- assumed API shapes -----------------------------------------------------------------
// This endpoint does not exist yet. The types below are the response the screen needs, named
// the way the rest of the codebase names things (camelCase, `id`, `<verb>At`, money as
// `numeric(14,2)` strings rather than floats).

/**
 * One row, carrying everything the row displays — the list must never fan out into a query
 * per customer for its balance or its phone.
 *
 * Money is `numeric(14,2)` in Postgres and therefore arrives as a string; it is converted
 * with `toCents` at this boundary and never touched as a float.
 */
type CustomerRow = {
  id: string;
  /** Human-facing customer number. Staff quote it on the phone. */
  number: string | null;
  displayName: string;
  organization: string | null;
  /** The customer's correspondence language, not the coordinator's interface language. */
  locale: Locale;
  status: 'active' | 'inactive' | 'archived';
  /** Primary phone as entered, so display fidelity survives; formatted with `formatPhone`. */
  phone: { raw: string; kind: string; extension: string | null } | null;
  /** Shown only when there is no phone at all, so no row ever looks uncontactable. */
  email: string | null;
  /** Primary service address. 84% of customers have one. */
  address: { line1: string; city: string; region: string; postalCode: string | null } | null;
  tags: Array<{ id: string; name: string; color: string }>;
  /** Sum of unpaid invoice balances. */
  openBalance: string;
  /** The part of `openBalance` already past its due date. */
  overdueBalance: string;
  /** Latest of: job, quote, invoice, payment, call, message. Null for a dormant record. */
  lastActivityAt: string | null;
};

/** `GET /api/customers` — cursor-paged, filtered, with the filter facets attached. */
type CustomersResponse = {
  items: CustomerRow[];
  /** Total matching the filters — not the page — so the count does not change as you page. */
  total: number;
  /** Null on the last page. Present for endpoints paged by cursor rather than by number. */
  nextCursor: string | null;
  /** Set when paging by number, which this list does. */
  page?: number;
  pageSize?: number;
  pageCount?: number;
  /** The ordering the API actually applied, so a heading cannot claim one it did not. */
  sort?: string;
  dir?: 'asc' | 'desc';
  /**
   * The values that actually occur under the current query. Returned with the list so the
   * filter controls cost no extra round trip and can never offer a choice that yields zero
   * rows.
   */
  facets: {
    cities: Array<{ city: string; count: number }>;
    tags: Array<{ id: string; name: string; color: string; count: number }>;
  };
};

/** How many rows of a preview a column of secondary text is worth. */
const PREVIEW_TAGS = 2;

export function CustomersScreen() {
  const { t: translate, i18n } = useTranslation();
  // Same cast the shell uses when handing `t` to a presentational component: the catalogue is
  // not type-augmented, and the rows only ever need `(key, vars) => string`.
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { q, tag, city } = useSearch({ from: '/customers' });
  const [exportNote, setExportNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);

  /*
   * `timeBased: false` — the directory offers a period but never opens with one.
   *
   * "Clients des 90 derniers jours" is not a question anybody asks, and applying the account's
   * default window here would hide 7 000 of 7 500 people for a reason nothing on the screen
   * could explain. The control is still there for "who did we take on this year".
   */
  const view = useListView({
    routeId: '/customers',
    defaultSort: 'recent',
    timeBased: false,
    defaultDateField: 'activity',
    extra: useMemo(() => ({ tag, city }), [tag, city]),
  });

  const list = useQuery({
    // `customers` is the prefix the realtime map invalidates for any customer change, so a
    // colleague editing a record refreshes this list without a bespoke subscription here.
    queryKey: ['customers', view.params.toString()],
    queryFn: ({ signal }) => request<CustomersResponse>(`/customers?${view.params}`, { signal }),
    // Keep the previous page on screen while the next one loads: a table that blanks out
    // between keystrokes reads as broken even when it is fast.
    placeholderData: (prev) => prev,
  });

  /**
   * The screen's own filters. The toolbar owns the search text and the period; these two are
   * the customer directory's, so it clears them.
   */
  const setFilters = useCallback(
    (patch: Record<string, unknown>) => {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, ...patch, cursor: undefined, page: undefined }),
        // Replacing rather than pushing: three filter adjustments must not cost three presses
        // of the back button to leave the screen.
        replace: true,
      });
    },
    [navigate],
  );

  const data = list.data;
  const rows = data?.items ?? [];
  const hasFilters = Boolean(q?.trim() || tag || city);

  const chips: FilterChip[] = [
    ...(city ? [{ id: 'city', label: `${t('customer.city')} : ${city}`, onRemove: () => setFilters({ city: undefined }) }] : []),
    ...(tag
      ? [
          {
            id: 'tag',
            label: `${t('customer.tags')} : ${data?.facets.tags.find((entry) => entry.id === tag)?.name ?? tag}`,
            onRemove: () => setFilters({ tag: undefined }),
          },
        ]
      : []),
  ];

  const runExport = async () => {
    setExporting(true);
    setExportNote(null);
    try {
      const outcome = await downloadExport('/customers/export', view.exportParams, locale);
      setExportNote({
        text: outcome.truncated
          ? t('list.exportTruncated', { limit: outcome.rows, total: outcome.matched })
          : t('list.exportDone', { count: outcome.rows }),
        failed: outcome.truncated,
      });
    } catch {
      setExportNote({ text: t('list.exportFailed'), failed: true });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="screen screen--customers">
      <header className="screen__head">
        <h1 className="screen__title">{t('customer.many')}</h1>
        {/* Rendered unconditionally: a live region has to be in the document before its
            contents change, or the new count is never announced. */}
        <span className="screen__count tnum" aria-live="polite">
          {data ? new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA').format(data.total) : ''}
        </span>
        <div className="screen__actions">
          <NewCustomerButton label={t('customer.new')} />
        </div>
      </header>

      <ListToolbar
        view={view}
        searchPlaceholder={t('common.searchPlaceholder')}
        chips={chips}
        onClearAll={() => setFilters({ tag: undefined, city: undefined })}
        dateFields={[
          { value: 'activity', label: t('list.field.activity') },
          { value: 'created', label: t('list.field.created') },
        ]}
        total={data?.total}
        onExport={() => void runExport()}
        exporting={exporting}
        exportNote={exportNote?.text ?? null}
        exportFailed={exportNote?.failed}
      >
        <label className="listbar__field">
          {t('customer.city')}
          <select value={city ?? ''} onChange={(e) => setFilters({ city: e.target.value || undefined })}>
            <option value="">{t('common.all')}</option>
            {data?.facets.cities.map((entry) => (
              <option key={entry.city} value={entry.city}>
                {entry.city} ({entry.count})
              </option>
            ))}
          </select>
        </label>

        <label className="listbar__field">
          {t('customer.tags')}
          <select value={tag ?? ''} onChange={(e) => setFilters({ tag: e.target.value || undefined })}>
            <option value="">{t('common.all')}</option>
            {data?.facets.tags.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name} ({entry.count})
              </option>
            ))}
          </select>
        </label>
      </ListToolbar>

      {list.isError && (
        <div className="notice notice--error" role="alert">
          <p>{t('errors.generic')}</p>
          <button type="button" onClick={() => void list.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* The table is dropped entirely when there is nothing in it: column headings above a
          void are furniture, and the empty state below says more. */}
      {(list.isPending || rows.length > 0) && (
        <table className="table table--dense table--customers" aria-label={t('customer.many')}>
          {/* Fixed proportions: the phone column must not reflow between renders, or the eye
              loses the column it was scanning down. */}
          <colgroup>
            <col style={{ width: '30%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '26%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>

          <thead>
            <tr>
              <SortHeader field="name" label={t('customer.name')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <th scope="col">
                <span className="label">{t('customer.phone')}</span>
              </th>
              <th scope="col">
                <span className="label">{t('customer.address')}</span>
              </th>
              <SortHeader
                field="balance"
                label={t('customer.balance')}
                sort={data?.sort}
                dir={data?.dir}
                onSort={view.setSort}
                numeric
              />
              <SortHeader
                field="recent"
                label={t('customer.lastActivity')}
                sort={data?.sort}
                dir={data?.dir}
                onSort={view.setSort}
              />
            </tr>
          </thead>

          <tbody>
            {list.isPending && (
              <tr>
                <td colSpan={5} className="cell cell--loading">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <Row key={row.id} row={row} locale={locale} t={t} />
            ))}
          </tbody>
        </table>
      )}

      {/* The empty state is where a coordinator lands when a customer is not in the system
          yet — which is exactly the moment to offer creating one, rather than apologising. */}
      {!list.isPending && rows.length === 0 && (
        <div className="empty">
          <h2 className="empty__title">{hasFilters ? t('common.noResults') : t('customer.emptyTitle')}</h2>
          <p className="empty__body">{hasFilters ? t('customer.emptyFilteredBody') : t('customer.emptyBody')}</p>
          <div className="empty__actions">
            <NewCustomerButton label={t('customer.new')} />
            {hasFilters && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setFilters({ tag: undefined, city: undefined });
                  view.set({ q: undefined });
                }}
              >
                {t('common.clearFilters')}
              </button>
            )}
          </div>
        </div>
      )}

      {data && (
        <Pager
          page={data.page ?? view.page}
          pageCount={data.pageCount ?? 1}
          pageSize={data.pageSize ?? view.pageSize}
          total={data.total}
          count={rows.length}
          label={t('customer.many').toLowerCase()}
          busy={list.isFetching}
          onPage={view.setPage}
          onPageSize={view.setPageSize}
        />
      )}
    </div>
  );
}

function Row({
  row,
  locale,
  t,
}: {
  row: CustomerRow;
  locale: Locale;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const openBalance = toCents(row.openBalance);
  const overdue = toCents(row.overdueBalance);
  const e164 = row.phone ? toE164(row.phone.raw) : null;

  return (
    <tr className={row.status === 'active' ? undefined : 'tr--inactive'}>
      <th scope="row" className="cell cell--name">
        <Link to="/customers/$customerId" params={{ customerId: row.id }} className="cell__link">
          {row.displayName}
        </Link>
        {/* The organisation is the second half of the identity for commercial accounts —
            two "Tremblay" rows are told apart by the company, not by the customer number. */}
        {row.organization && <span className="cell__sub">{row.organization}</span>}
        {row.tags.length > 0 && (
          <span className="cell__tags">
            {row.tags.slice(0, PREVIEW_TAGS).map((label) => (
              <span key={label.id} className="tag" style={{ ['--chip' as string]: label.color }}>
                {label.name}
              </span>
            ))}
            {row.tags.length > PREVIEW_TAGS && (
              <span className="tag tag--more tnum">+{row.tags.length - PREVIEW_TAGS}</span>
            )}
          </span>
        )}
      </th>

      <td className="cell cell--phone">
        {/* Click-to-call from the list: a coordinator returning a missed call should not have
            to open the customer first. */}
        {row.phone && e164 ? (
          <a
            href={`tel:${e164}`}
            className="phone"
            aria-label={`${t('calls.dial')} ${formatPhone(row.phone.raw, locale)} — ${row.displayName}`}
          >
            {formatPhone(row.phone.raw, locale)}
            {row.phone.extension && <span className="phone__ext"> p. {row.phone.extension}</span>}
          </a>
        ) : row.email ? (
          <a href={`mailto:${row.email}`} className="cell__fallback">
            {row.email}
          </a>
        ) : (
          <span className="cell__none" aria-hidden="true">
            —
          </span>
        )}
      </td>

      <td className="cell cell--address">
        {row.address ? (
          <>
            {/* City first: work is dispatched and grouped by city, so it is the part that
                gets scanned. The street is the confirmation, not the search key. */}
            <span className="cell__city">{row.address.city}</span>
            <span className="cell__sub">{row.address.line1}</span>
          </>
        ) : (
          <span className="cell__none" aria-hidden="true">
            —
          </span>
        )}
      </td>

      <td className="cell cell--balance numeric">
        {openBalance > 0 ? (
          <>
            <span className={`money${overdue > 0 ? ' money--overdue' : ' money--due'}`}>
              {formatMoney(openBalance, locale)}
            </span>
            {overdue > 0 && (
              <span className="cell__sub cell__sub--overdue">
                {t('customer.overduePortion', { amount: formatMoney(overdue, locale) })}
              </span>
            )}
          </>
        ) : (
          <span className="cell__none" aria-hidden="true">
            —
          </span>
        )}
      </td>

      <td className="cell cell--activity">
        <LastActivity iso={row.lastActivityAt} locale={locale} t={t} />
      </td>
    </tr>
  );
}

/**
 * Last contact, relative.
 *
 * "il y a 3 j" answers the question actually being asked — is this relationship warm or
 * cold — where a timestamp would have to be read and subtracted. The exact date stays in the
 * tooltip for the rarer moment when precision matters.
 */
function LastActivity({
  iso,
  locale,
  t,
}: {
  iso: string | null;
  locale: Locale;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  if (!iso) {
    return <span className="cell__none">{t('common.none')}</span>;
  }

  const intlLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
  const at = new Date(iso);
  const days = Math.floor((Date.now() - at.getTime()) / 86_400_000);

  const label =
    days <= 0
      ? t('common.today')
      : days === 1
        ? t('common.yesterday')
        : days < 30
          ? new Intl.RelativeTimeFormat(intlLocale, { numeric: 'auto', style: 'short' }).format(-days, 'day')
          : new Intl.DateTimeFormat(intlLocale, { month: 'short', year: 'numeric' }).format(at);

  return (
    <time
      dateTime={iso}
      className={days > 365 ? 'activity activity--cold' : 'activity'}
      title={new Intl.DateTimeFormat(intlLocale, { dateStyle: 'long', timeStyle: 'short' }).format(at)}
    >
      {label}
    </time>
  );
}

/**
 * Creating a customer.
 *
 * A `Link`, not a button: creating a customer is a place, so it should be openable in a new
 * tab and reachable by the back button like anywhere else. It stays visible in the empty state
 * because an empty list with nothing to press is a dead end.
 */
function NewCustomerButton({ label }: { label: string }) {
  return (
    <Link to="/customers/new" className="btn btn--primary" aria-label={label}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M6 1.5v9M1.5 6h9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      </svg>
      {label}
    </Link>
  );
}
